import { useState, useCallback, useMemo } from 'react';

import {
  useGetOverallTradingAccount,
  OverallTradingAccountFilter,
  OverallTradingAccountApiResponse,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseOverallTradingAccountOptions {
  recordId?: number;
}

const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(oneYearAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

export interface TradingAccountItem {
  name: string;
  amount: number;
}

export interface MappedOverallTradingAccountData {
  debitSide: {
    items: TradingAccountItem[];
    total: number;
  };
  creditSide: {
    items: TradingAccountItem[];
    total: number;
  };
}

const useOverallTradingAccount = (options: UseOverallTradingAccountOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  const defaultDates = getDefaultDateRange();

  const [filters, setFilters] = useState<Partial<OverallTradingAccountFilter>>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  const hasRequiredParams = !!recordId && !!userId && !!filters.start_date && !!filters.end_date;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetOverallTradingAccount({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  const rawData = useMemo(() => data?.data, [data?.data]);

  const parseAmount = (value: number | string): number => {
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    return value;
  };

  const mappedData = useMemo((): MappedOverallTradingAccountData | null => {
    if (!rawData) return null;

    const response = rawData as OverallTradingAccountApiResponse;

    const debitItems: TradingAccountItem[] = [];
    if (response.debit) {
      Object.entries(response.debit).forEach(([name, value]) => {
        debitItems.push({ name, amount: parseAmount(value) });
      });
    }

    const creditItems: TradingAccountItem[] = [];
    if (response.credit) {
      Object.entries(response.credit).forEach(([name, value]) => {
        creditItems.push({ name, amount: parseAmount(value) });
      });
    }

    return {
      debitSide: {
        items: debitItems,
        total: response.balance_dr ?? 0,
      },
      creditSide: {
        items: creditItems,
        total: response.balance_cr ?? 0,
      },
    };
  }, [rawData]);

  const updateFilter = useCallback((updates: Partial<OverallTradingAccountFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
    });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  return {
    rawData,
    mappedData,
    isLoading,
    hasRequiredParams,
    error,
    filters,
    handleDateFilter,
    resetFilters,
    updateFilter,
    refetch,
  };
};

export default useOverallTradingAccount;
